<template>
  <div class="card">
    <div class="card-body table-responsive">
      <b-table
        striped
        bordered
        foot-clone
        responsive="sm"
        :fields="fields"
        class="datatables"
        :items="items.data"
        :tbody-tr-class="rowClass"
      >
        <!-- Seus templates permanecem os mesmos -->
        <template v-if="!importacao" v-slot:cell(ST_PROJETO)="data">
          <span
            class="badge badge-pill font-size-12"
            :class="{
              'badge-soft-info': data.value === 'ELABORADO',
              'badge-soft-primary' : data.value === 'INCOMPLETO',
              'badge-soft-success': data.value === 'LIBERADO',
              'badge-soft-dark': data.value === 'LIQUIDADO',
              'badge-soft-danger': data.value === 'EXCLUIDO',
              'badge-soft-warning': data.value === 'BAIXA PARCIAL',
            }"
          >
            {{ data.value }}
          </span>
        </template>

        <template v-else v-slot:cell(ST_IMPORTACAO)="data">
          <span
            class="badge badge-pill font-size-12"
            :class="{
              'badge-soft-info': data.value === 'COMPLETO',
              'badge-soft-success': data.value === 'EXPORTADO',
              'badge-soft-danger': data.value === 'INCOMPLETO',
            }"
          >
            {{ data.value }}
          </span>
        </template>

        <template #cell(interacao)="data">
          <div v-if="(data.item.ST_PROJETO !== 'ELABORADO' && !importacao) || (importacao && data.item.ST_IMPORTACAO === 'EXPORTADO')">
            <b-button size="sm" variant="primary" class="me-1" :disabled="loadingAcoes" data-tooltip="Visualizar" data-tooltip-location="left" @click="emitirEditarItem(data.item)">
              <i class="mdi mdi-eye"></i>
            </b-button>
          </div>
          <div class="d-flex justify-content-start" v-else>
            <b-button size="sm" variant="primary" class="me-1" :disabled="loadingAcoes" data-tooltip="Editar" data-tooltip-location="left" @click="emitirEditarItem(data.item)">
              <i class="mdi mdi-pencil"></i>
            </b-button>
            <b-button v-if="!importacao" size="sm" variant="danger" data-tooltip="Excluir" :disabled="loadingAcoes" data-tooltip-location="left" @click="deletarProjeto(data.item)">
              <i class="mdi mdi-trash-can"></i>
              <b-spinner
                variant="primary"
                small
                class="ms-2"
                v-if="loadingAcoes"
              ></b-spinner>
            </b-button>
          </div>
        </template>

        <!-- Templates do footer -->
        <template #foot()="">
          <h6></h6>
        </template>
        <template #foot(NM_PRODUTOR_RURAL)="">
          <div class="text-end">
            <span class="text-dark fw-bold">TOTAIS:</span>
          </div>
        </template>
        <template #foot(VL_ORCADO)="">
          <div class="text-end">
            <span class="text-dark fw-bold">{{ $tools.converteReal(totalVlOrcado) }}</span>
          </div>
        </template>
        <template #foot(VL_ASTEC)="">
          <div class="text-end">
            <span class="text-dark fw-bold">{{ $tools.converteReal(totalVlAstec) }}</span>
          </div>
        </template>
        <template #foot(VL_LIBERADO)="">
          <div class="text-end">
            <span class="text-dark fw-bold">{{ $tools.converteReal(totalVlLiberado) }}</span>
          </div>
        </template>
        <template #foot(VL_SALDO_FOLHA)="">
          <div class="text-end">
            <span class="text-dark fw-bold">{{ $tools.converteReal(totalVlSaldoFolha) }}</span>
          </div>
        </template>
        <template #foot(VL_SALDO_RDV)="">
          <div class="text-end">
            <span class="text-dark fw-bold">{{ $tools.converteReal(totalVlSaldoRdv) }}</span>
          </div>
        </template>
      </b-table>

      <!-- Paginação -->
      <div class="d-flex justify-content-end mt-3" v-if="items.data && items.data.length > 0">
        <b-pagination
          v-model="currentPage"
          @change="onPageChange"
          :per-page="itemsPerPage"
          :total-rows="items.total || 0"
          class="pagination pagination-rounded mb-0"
        />
      </div>

      <!-- Mensagem quando não há dados -->
      <div v-if="!items.data || items.data.length === 0" class="text-center p-4">
        <p class="text-muted">Nenhum projeto encontrado</p>
      </div>
    </div>
  </div>
</template>

<script>
import Swal from "sweetalert2";
import clienteRuralMixin from "@/tools/mixins/clienteRuralMixin.js";

export default {
  mixins: [clienteRuralMixin],
  name: 'Grid',
  props: {
    filtro: {
      type: Object,
      default: () => ({})
    },
    items: {
      type: Object,
      default: () => ({
        data: [],
        total: 0,
        current_page: 1
      })
    },
    importacao: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      fields: [
        { key: 'NR_PROJETO', label: 'N° Projeto', sortable: true },
        { key: 'NM_INSTITUICAO_FINANCEIRA', label: 'Instituição Financeira', sortable: true },
        { key: 'NM_LINHA_CREDITO', label: 'Linha Crédito', sortable: true },
        { key: 'CD_SAFRA', label: 'Safra', sortable: true },
        { key: 'DS_TP_PROJETO', label: 'Tipo Projeto', sortable: true },
        { key: 'DS_ITEM_AGROPECUARIO', label: 'Item Agropecuário', sortable: true },
        { key: 'NM_PRODUTOR_RURAL', label: 'Cliente Rural', sortable: true },
        {
          key: 'VL_ORCADO',
          label: 'Valor Orçado',
          sortable: true,
          formatter: (value) => this.$tools.converteReal(value),
        },
        {
          key: 'VL_ASTEC',
          label: 'Valor Astec',
          sortable: true,
          formatter: (value) => this.$tools.converteReal(value),
        },
        {
          key: 'VL_LIBERADO',
          label: 'Valor Liberado',
          sortable: true,
          formatter: (value) => this.$tools.converteReal(value),
        },
        {
          key: 'VL_SALDO_FOLHA',
          label: 'Saldo FOL',
          sortable: true,
          formatter: (value) => this.$tools.converteReal(value),
        },
        {
          key: 'VL_SALDO_RDV',
          label: 'Saldo RDV',
          sortable: true,
          formatter: (value) => this.$tools.converteReal(value),
        },
        { key: 'ST_PROJETO', label: 'Status', sortable: true },
        { key: 'interacao', label: 'Ações' }
      ],
      totalRows: 0,
      currentPage: 1,
      itemsPerPage: 10,
      isLoading: false,
      loadingAcoes: false,

    };
  },

  computed: {
    totalVlOrcado() {
      return this.calcularTotal('VL_ORCADO');
    },
    totalVlAstec() {
      return this.calcularTotal('VL_ASTEC');
    },
    totalVlLiberado() {
      return this.calcularTotal('VL_LIBERADO');
    },
    totalVlSaldoFolha() {
      return this.calcularTotal('VL_SALDO_FOLHA');
    },
    totalVlSaldoRdv() {
      return this.calcularTotal('VL_SALDO_RDV');
    }
  },

  watch: {
    items: {
      deep: true,
      handler(newVal) {
        if (newVal && newVal.current_page) {
          this.currentPage = newVal.current_page;
        }
      },
      immediate: true
    },
  },

  methods: {
    rowClass(item, type) {
      if (!item || type !== 'row') return '';
      if (item.ST_PROJETO === 'EXCLUIDO') {
        return 'table-danger';
      }
      return '';
    },

    calcularTotal(campo) {
      if (!this.items.data || this.items.data.length === 0) return 0;

      return this.items.data.reduce((total, item) => {
        return total + parseFloat(item[campo] || 0);
      }, 0);
    },

    emitirEditarItem(data) {
      this.$store.commit("PARAMETROS_PROJETO_ASTEC", data);
      localStorage.setItem('exibirCamposImportacao', this.importacao.toString());
      this.$router.push('/astec/projetos/novoProjeto');
    },

    deletarProjeto(projeto) {
      Swal.fire({
        title: "Tem certeza?",
        text: "Você realmente deseja excluir este projeto? Essa ação não pode ser desfeita.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Sim, excluir!",
        cancelButtonText: "Cancelar",
      }).then((result) => {
        if (result.isConfirmed) {
          this.loadingAcoes = true;

          const obj = {
            sqUsuario: this.$store.state.user,
            cdCooperado: projeto.CD_COOPERADO,
            sqProjetoVar: projeto.SQ_PROJETO_VAR,
          };

          this.$http.post("/api/astec/deletarProjeto", obj)
            .then(() => {
              Swal.fire({
                icon: "success",
                title: "Sucesso",
                text: "Projeto excluído com sucesso!",
              });

              // Recarrega os dados mantendo a página atual
              this.$emit('projetoAstecFiltrados', {
                ...this.filtro,
                page: this.currentPage,
                per_page: this.itemsPerPage
              });
            })
            .catch(err => {
              this.$tools.alerta(err.response?.data?.message || "Erro ao excluir projeto", "error");
            })
            .finally(() => {
              this.loadingAcoes = false;
            });
        }
      });
    },

    created() {
      if (this.importacao && !this.fields.find(f => f.key === 'ST_IMPORTACAO')) {
        this.fields.splice(12, 1, {
          key: 'ST_IMPORTACAO',
          label: 'Status da Importação',
          sortable: true
        });
      }
    },

    onPageChange(page) {
      this.currentPage = page;
      this.$emit('projetoAstecFiltrados', {
        ...this.filtro,
        page: this.currentPage,
        per_page: this.itemsPerPage
      });
    },
  },

};
</script>

<style scoped>
.sortable-header {
  cursor: pointer;
  display: flex;
  align-items: center;
}

.sort-icon {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-left: 5px;
}
</style>
